import { Component, inject, OnInit } from '@angular/core';
import { CommonModule } from '@angular/common';
import { TranslateModule } from '@ngx-translate/core';
import { ButtonComponent } from '@akostm/orbit-ui';
import { faEdit, faPlus } from '@fortawesome/free-solid-svg-icons';
import { Dialog } from '@angular/cdk/dialog';
import { ConversationTypesDialogComponent } from './conversation-types-dialog/conversation-types-dialog.component';
import { ConversationTypesService } from './conversation-types.service';
import {
  ConversationType,
  ConversationTypePostRequest,
  ConversationTypePutRequest,
} from '@clientwire/api';

@Component({
  selector: 'lib-conversation-types',
  standalone: true,
  imports: [CommonModule, TranslateModule, ButtonComponent],
  templateUrl: './conversation-types.component.html',
  styleUrl: './conversation-types.component.scss',
})
export class ConversationTypesComponent implements OnInit {
  conversationTypesService: ConversationTypesService = inject(ConversationTypesService);

  loading = this.conversationTypesService.loading;
  conversationTypes = this.conversationTypesService.conversationTypes;

  faEdit = faEdit;
  faPlus = faPlus;

  constructor(private dialog: Dialog) {}

  ngOnInit(): void {
    this.conversationTypesService.loadConversationTypes();
  }

  onAdd(): void {
    const dialogRef = this.dialog.open(ConversationTypesDialogComponent, {
      data: { error: this.conversationTypesService.error },
    });
    dialogRef.componentInstance?.dialogButtonClicked.subscribe(async (form) => {
      if (form.eventType === 'SAVE' && form.data) {
        const result = form.data as ConversationTypePostRequest;
        if (result.conversationDataSchema) {
          result.conversationDataSchema = JSON.parse(result.conversationDataSchema);
        }
        if (!result.clientAppHeaderTemplate) {
          result.clientAppHeaderTemplate = null;
        }
        await this.conversationTypesService.createConversationType({
          conversationTypePostRequest: {
            id: result.id,
            description: result.description,
            displayNameSharedByUsers: result.displayNameSharedByUsers,
            displayNameForWorkflowParticipants: result.displayNameForWorkflowParticipants,
            conversationDataSchema: result.conversationDataSchema,
            conversationDataListItemTemplate: result.conversationDataListItemTemplate ?? '',
            clientAppHeaderTemplate: result.clientAppHeaderTemplate,
            smsSenderId: result.smsSenderId,
            retentionPolicyDays: result.retentionPolicyDays ? Number(result.retentionPolicyDays) : null,
            messageTemplates: result.messageTemplates,
          },
        });
        if (!this.conversationTypesService.error()) {
          dialogRef.close();
        }
      }
      if (form.eventType === 'CANCEL') {
        this.conversationTypesService.clearErrors();
        dialogRef.close();
      }
    });
  }

  onEdit(type: ConversationType): void {
    const dialogRef = this.dialog.open(ConversationTypesDialogComponent, {
      data: { conversationType: type, error: this.conversationTypesService.error },
    });
    dialogRef.componentInstance?.dialogButtonClicked.subscribe(async (form) => {
      if (form.eventType === 'SAVE' && form.data) {
        const result = form.data as ConversationTypePutRequest;
        if (result.conversationDataSchema) {
          result.conversationDataSchema = JSON.parse(result.conversationDataSchema);
        }
        if (!result.clientAppHeaderTemplate) {
          result.clientAppHeaderTemplate = null;
        }
        await this.conversationTypesService.createOrUpdateConversationType({
          conversationTypeId: type.id,
          conversationTypePutRequest: {
            id: result.id,
            description: result.description,
            displayNameSharedByUsers: result.displayNameSharedByUsers,
            sharedReadStatusForUserParticipants: result.sharedReadStatusForUserParticipants,
            displayNameForWorkflowParticipants: result.displayNameForWorkflowParticipants,
            conversationDataSchema: result.conversationDataSchema,
            conversationDataListItemTemplate: result.conversationDataListItemTemplate ?? '',
            clientAppHeaderTemplate: result.clientAppHeaderTemplate,
            smsSenderId: result.smsSenderId,
            retentionPolicyDays: result.retentionPolicyDays ? Number(result.retentionPolicyDays) : null,
            messageTemplates: result.messageTemplates,
          },
        });
        if (!this.conversationTypesService.error()) {
          dialogRef.close();
        }
      }
      if (form.eventType === 'CANCEL') {
        this.conversationTypesService.clearErrors();
        dialogRef.close();
      }
    });
  }
}
